import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTabActive } from "../../lib/tabActive";
import { Icon } from "../ui/Icon";

/**
 * Formu başka tesise (veya Global'e) kopyalama modalı — süper-admin.
 * ConfirmDialog/PromptDialog ile aynı .cdlg-* iskeleti. Hedef tesis + (opsiyonel) yeni ad seçilir;
 * kopya sunucuda DAİMA taslak olarak oluşturulur ve sonra Düzenle ile serbestçe değiştirilir.
 */
const GLOBAL = "__global__"; // select değeri: Global (tesisId=null)

export type CopySource = { id: string; name: string; tesisId: string | null };

export function FormCopyDialog({
  open, source, facilities, busy = false, onConfirm, onCancel,
}: {
  open: boolean;
  source: CopySource | null;
  facilities: { id: string; name: string }[];
  busy?: boolean;
  onConfirm: (args: { targetTesisId: string | null; name: string }) => void;
  onCancel: () => void;
}) {
  const [target, setTarget] = useState(""); // "" = seçilmedi, GLOBAL, veya facility.id
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Açılışta ad'ı kaynaktan doldur, hedefi/hata durumunu sıfırla.
  useEffect(() => {
    if (!open) return;
    setName(source?.name ?? "");
    setTarget("");
    setError(null);
  }, [open, source]);

  // ESC kapatır.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const tabActive = useTabActive();
  if (!open || !source) return null;
  // PASİF SEKMEDE DOM'A BASILMAZ. Portal `document.body`ye gider, yani sekme panelinin
  // `display:none` gizlemesi bu katmana ULAŞMAZ — gizli sekmenin paneli aktif sayfanın
  // üstünde durmaya devam ederdi. Burada `null` dönmek DOM'u düşürür, katmanı açan durum
  // (hangi kayıt, forma ne yazıldı) sayfanın React state'inde YAŞAMAYA DEVAM eder.
  if (!tabActive) return null;


  function submit() {
    if (busy) return; // çift-gönderim koruması (Enter, disabled butonu atlar)
    if (!target) { setError("Hedef tesis seçin."); return; }
    if (!name.trim()) { setError("Form adı zorunlu."); return; }
    onConfirm({ targetTesisId: target === GLOBAL ? null : target, name: name.trim() });
  }

  return createPortal(
    <div className="cdlg-backdrop" role="presentation" onClick={onCancel}>
      <div className="cdlg" role="dialog" aria-modal="true" aria-label="Formu Kopyala" onClick={(e) => e.stopPropagation()}>
        <span className="cdlg-icon primary" aria-hidden="true"><Icon name="copy" size={20} /></span>
        <div className="cdlg-body">
          <h3 className="cdlg-title">Formu Kopyala</h3>
          <div className="cdlg-msg" style={{ marginBottom: 12 }}>
            <strong>{source.name}</strong> formu seçilen tesise <em>taslak</em> olarak kopyalanır; sonra Düzenle ile içeriğini serbestçe değiştirebilirsiniz.
          </div>
          <div className="form-field" style={{ marginBottom: 12 }}>
            <label className="lbl" htmlFor="copy-target">Hedef tesis<span className="req">*</span></label>
            <select id="copy-target" className={`form-select${error && !target ? " err" : ""}`} value={target} disabled={busy} onChange={(e) => { setTarget(e.target.value); if (error) setError(null); }}>
              <option value="" disabled>Hedef tesis seçin…</option>
              <option value={GLOBAL}>Global (tüm tesisler){source.tesisId == null ? " · kaynak" : ""}</option>
              {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}{source.tesisId === f.id ? " · kaynak" : ""}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="lbl" htmlFor="copy-name">Yeni form adı<span className="req">*</span></label>
            <input id="copy-name" className={`form-input${error && !name.trim() ? " err" : ""}`} value={name} maxLength={200} disabled={busy} placeholder={source.name} onChange={(e) => { setName(e.target.value); if (error) setError(null); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }} />
          </div>
          {error ? <div className="form-field" style={{ margin: "8px 0 0" }}><span className="err" role="alert">{error}</span></div> : null}
        </div>
        <div className="cdlg-foot">
          <button type="button" className="btn-md secondary" onClick={onCancel} disabled={busy}>Vazgeç</button>
          <button type="button" className="btn-md primary" onClick={submit} disabled={busy}><Icon name="copy" size={14} />{busy ? "Kopyalanıyor…" : "Kopyala"}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
